import { jwtDecode } from 'jwt-decode';
import UserAuth from '../auth';
import {
  ACTIVITY_LOGGER_DEBUG,
  EVENT_TRACKER_URL,
  FLUSH_LOCK_NAME,
  MAX_EVENTS_PER_BATCH,
  MAX_KEEPALIVE_BATCH_BYTES,
  MAX_SERVER_ERROR_RETRIES,
  USER_EVENTS_PATH,
} from './constants';
import { getQueuedEvents, removeQueuedEvents } from './storage';
import { AuthHandlers, FlushMode, FlushResult, QueuedEvent, UserEvent } from './types';

// Error codes returned with 401 by the event tracker
const ERROR_CODES = {
  // The token has no sub claim (token configuration problem)
  NO_SUB_CLAIM: 3003,
  // The token is valid but the user has no active pvx-core session (e.g. logged out)
  NO_PVX_CORE_SESSION: 3004,
};

// Only the API's fields are sent; userId stays in the browser
const toUserEvent = ({
  eventId,
  sessionId,
  resourceType,
  resourceId,
  eventType,
  eventName,
  occurredAt,
  eventDetails,
}: QueuedEvent): UserEvent => ({
  eventId,
  sessionId,
  resourceType,
  resourceId,
  eventType,
  eventName,
  occurredAt,
  eventDetails,
});

const toBody = (events: QueuedEvent[]): string =>
  JSON.stringify({ events: events.map(toUserEvent) });

// Events in keepalive requests of this tab that haven't finished. Such a request survives the page
// closing, so its events are left out of other requests meanwhile.
const keepaliveInFlight = new Set<string>();

// Up to 100 queued events not already in flight, halved until the body fits maxBytes
const takeBatch = (userId: string, maxBytes: number): { batch: QueuedEvent[]; body: string } => {
  const pending = getQueuedEvents(userId).filter((event) => !keepaliveInFlight.has(event.eventId));
  let batch = pending.slice(0, MAX_EVENTS_PER_BATCH);
  let body = toBody(batch);

  while (batch.length > 1 && new Blob([body]).size > maxBytes) {
    batch = batch.slice(0, Math.ceil(batch.length / 2));
    body = toBody(batch);
  }

  return { batch, body };
};

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return Boolean(exp && exp * 1000 <= Date.now());
  } catch {
    return true;
  }
};

const readErrorCode = async (response: Response): Promise<number | undefined> => {
  try {
    return (await response.json())?.code;
  } catch {
    return undefined; // 401 for an expired token has an empty body
  }
};

const post = (body: string, token: string, keepalive: boolean): Promise<Response> =>
  fetch(`${EVENT_TRACKER_URL}${USER_EVENTS_PATH}`, {
    method: 'POST',
    // keepalive lets the request finish after the page closes (sendBeacon can't send the token)
    keepalive,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body,
  });

// One refresh at a time, shared by the sends of this tab
let refreshing: Promise<string | null> | null = null;

const refreshOnce = (auth: AuthHandlers): Promise<string | null> => {
  if (!refreshing) {
    refreshing = auth.refreshAccessToken().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
};

// 401 that a new token can fix: token missing, expired or invalid (empty body), or no active
// pvx-core session (3004). A token without a sub claim (3003) is a configuration problem.
const needsReauth = (response: Response, code: number | undefined): boolean =>
  response.status === 401 && code !== ERROR_CODES.NO_SUB_CLAIM;

// Counts 500 responses for the batch at the front of the queue
let serverErrors = { firstEventId: '', count: 0 };

const sendBatch = async (
  userId: string,
  mode: FlushMode,
  auth: AuthHandlers,
): Promise<FlushResult> => {
  const token = UserAuth.accessToken;
  if (!token) return 'unauthorized';

  // A closing page can't refresh the token, so the events wait for the next visit
  if (mode === 'closing' && isTokenExpired(token)) return 'unauthorized';

  const keepalive = mode !== 'normal';
  // Only keepalive requests have a size limit (normal requests are capped at 100 events)
  const { batch, body } = takeBatch(userId, keepalive ? MAX_KEEPALIVE_BATCH_BYTES : Infinity);
  const eventIds = batch.map((event) => event.eventId);

  if (ACTIVITY_LOGGER_DEBUG && batch.length) {
    console.info('[activityLogger] sending', mode, batch.map(toUserEvent));
  }

  if (mode === 'closing') {
    // Only events not yet in flight are sent: the request made when the tab was hidden survives
    // the page closing. The page can't read responses any more, so the events of both requests
    // are removed now; resending them on the next visit would be too late for the server's
    // duplicate check (about a minute).
    if (batch.length) post(body, token, true).catch(() => undefined);
    removeQueuedEvents([...keepaliveInFlight, ...eventIds]);
    return batch.length ? 'accepted' : 'empty';
  }

  if (!batch.length) return 'empty';
  if (keepalive) eventIds.forEach((eventId) => keepaliveInFlight.add(eventId));

  try {
    let response = await post(body, token, keepalive);
    let code = response.ok ? undefined : await readErrorCode(response);

    // Re-authenticates like the other APIs and retries once. If the refresh fails, or the user
    // still has no pvx-core session, they are sent to log in; the batch waits for the next login.
    if (needsReauth(response, code)) {
      const newToken = await refreshOnce(auth);
      if (!newToken) {
        auth.navigateToAuth();
        return 'unauthorized';
      }

      response = await post(body, newToken, keepalive);
      code = response.ok ? undefined : await readErrorCode(response);

      if (response.status === 401 && code === ERROR_CODES.NO_PVX_CORE_SESSION) {
        auth.navigateToAuth();
        return 'unauthorized';
      }

      // A new token rejected as well points to the event tracker, not the user's session (other
      // APIs don't log the user out here either): kept and retried with backoff
      if (needsReauth(response, code)) return 'retry';
    }

    if (ACTIVITY_LOGGER_DEBUG) console.info('[activityLogger] response', response.status, code);

    if (response.ok) {
      removeQueuedEvents(eventIds);
      return 'accepted';
    }

    // Invalid batch, or a token without a sub claim: resending would fail the same way
    if (response.status === 400 || (response.status === 401 && code === ERROR_CODES.NO_SUB_CLAIM)) {
      console.error('[activityLogger] Batch rejected', response.status, code);
      removeQueuedEvents(eventIds);
      return 'dropped';
    }

    // Unexpected server error: retried a limited number of times
    if (response.status === 500) {
      serverErrors =
        serverErrors.firstEventId === eventIds[0]
          ? { firstEventId: eventIds[0], count: serverErrors.count + 1 }
          : { firstEventId: eventIds[0], count: 1 };

      if (serverErrors.count > MAX_SERVER_ERROR_RETRIES) {
        console.error('[activityLogger] Batch dropped after repeated server errors');
        removeQueuedEvents(eventIds);
        return 'dropped';
      }
    }

    // 503 and other temporary errors: kept and retried with backoff, with the same eventIds
    return 'retry';
  } catch {
    // Network error: kept and retried with backoff
    return 'retry';
  } finally {
    eventIds.forEach((eventId) => keepaliveInFlight.delete(eventId));
  }
};

// Sends the user's oldest queued events. Tabs share the queue, so a lock keeps two tabs from
// sending the same batch; a hidden or closing page can't wait for the lock and sends directly.
export const flushEvents = async (
  userId: string,
  mode: FlushMode,
  auth: AuthHandlers,
): Promise<FlushResult> => {
  if (!userId) return 'empty';

  if (mode !== 'normal' || !navigator.locks) {
    return sendBatch(userId, mode, auth);
  }

  let result: FlushResult = 'empty';
  await navigator.locks.request(FLUSH_LOCK_NAME, { ifAvailable: true }, async (lock) => {
    if (lock) result = await sendBatch(userId, 'normal', auth);
  });
  return result;
};
